import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useApplyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { MoneyInput } from '@nucleus-commerce/admin-kit/forms';
import type { Product } from '@nucleus-commerce/api-client/admin';
import { isApiError } from '@nucleus-commerce/api-client/transport';
import type { Money } from '@nucleus-commerce/core';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@nucleus-commerce/ui/components/select';

import { BASE_CURRENCY } from '@/shared/catalog/base-currency';
import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { emptyNewVariantForm, toVariantCreate } from '../api/variant-mappers';
import { useCreateVariant } from '../hooks/use-variant-mutations';
import {
  newVariantFormSchema,
  type NewVariantFormInput,
  type NewVariantFormOutput,
} from '../schemas/variant-form-schema';

export interface AddVariantDialogProps {
  product: Product;
  defaultPrice: Money | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** The backend puts an unknown option value on `body.optionValues.<key>`; the form holds it under the same key. */
const fieldPath = (apiField: string) => apiField;

function DialogBody({
  product,
  defaultPrice,
  onClose,
}: {
  product: Product;
  defaultPrice: Money | null;
  onClose: () => void;
}) {
  const { t } = useTranslation(['products', 'common']);
  const message = useErrorMessage();
  const create = useCreateVariant(product.id);
  const hasOptions = product.options.length > 0;
  const form = useForm<NewVariantFormInput, unknown, NewVariantFormOutput>({
    resolver: useI18nResolver(newVariantFormSchema),
    defaultValues: emptyNewVariantForm(product, defaultPrice),
  });
  const applyFieldErrors = useApplyFieldErrors(form.setError, fieldPath);

  const submit = form.handleSubmit(async (values) => {
    form.clearErrors('root.server');
    try {
      await create.mutateAsync(toVariantCreate(values));
      onClose();
    } catch (error) {
      if (applyFieldErrors(error)) return;
      form.setError('root.server', {
        type: 'server',
        message:
          isApiError(error) && error.code === 'ALREADY_EXISTS'
            ? t('products:variants.add.taken')
            : message(error),
      });
    }
  });

  return (
    <Form {...form}>
      <form onSubmit={submit} noValidate className="flex flex-col gap-4">
        {form.formState.errors.root?.server?.message ? (
          <Alert variant="destructive" role="alert">
            <AlertDescription>{form.formState.errors.root.server.message}</AlertDescription>
          </Alert>
        ) : null}
        {hasOptions ? (
          <div className="grid gap-3 sm:grid-cols-2">
            {product.options.map((axis) => (
              <FormField
                key={axis.id}
                control={form.control}
                name={`optionValues.${axis.key}`}
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormLabel>{axis.name}</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger className="w-full" aria-invalid={fieldState.invalid}>
                          <SelectValue placeholder={t('products:variants.add.pickValue')} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {axis.values.map((value) => (
                          <SelectItem key={value.id} value={value.value}>
                            {value.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            ))}
          </div>
        ) : (
          <p className="text-muted-foreground text-sm">{t('products:variants.add.defaultHint')}</p>
        )}
        <div className="grid gap-3 sm:grid-cols-2">
          <FormField
            control={form.control}
            name="price"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel>{t('products:variants.columns.price')}</FormLabel>
                <FormControl>
                  <MoneyInput
                    currency={BASE_CURRENCY}
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    aria-invalid={fieldState.invalid}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="compareAt"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('products:variants.columns.compareAt')}</FormLabel>
                <FormControl>
                  <MoneyInput
                    currency={BASE_CURRENCY}
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="sku"
            render={({ field }) => (
              <FormItem className="sm:col-span-2">
                <FormLabel>{t('products:variants.columns.sku')}</FormLabel>
                <FormControl>
                  <Input {...field} className="font-mono text-[13px]" />
                </FormControl>
                <FormDescription>
                  {product.skuRule && product.skuRoot
                    ? t('products:variants.add.skuHintRule', {
                        rule: product.skuRule.name,
                        root: product.skuRoot,
                      })
                    : product.skuRoot
                      ? t('products:variants.add.skuHint', { root: product.skuRoot })
                      : t('products:variants.add.skuHintNoRoot')}
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onClose} disabled={create.isPending}>
            {t('common:actions.cancel')}
          </Button>
          <Button type="submit" disabled={create.isPending}>
            {create.isPending
              ? t('products:variants.add.working')
              : hasOptions
                ? t('products:variants.add.submit')
                : t('products:variants.add.submitDefault')}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}

/**
 * One variant by hand: a combination of the option values with its price and SKU, or, for a
 * product without options, the single default variant that makes it sellable.
 */
export function AddVariantDialog({
  product,
  defaultPrice,
  open,
  onOpenChange,
}: AddVariantDialogProps) {
  const { t } = useTranslation('products');
  const hasOptions = product.options.length > 0;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {hasOptions ? t('variants.add.title') : t('variants.add.titleDefault')}
          </DialogTitle>
          <DialogDescription>
            {hasOptions ? t('variants.add.description') : t('variants.add.descriptionDefault')}
          </DialogDescription>
        </DialogHeader>
        {open ? (
          <DialogBody
            product={product}
            defaultPrice={defaultPrice}
            onClose={() => onOpenChange(false)}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
